import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';
import { FIELD_ERROR } from '../form/field-styles';
import { uniqueId } from '../form/ids';
import { IconComponent } from '../icon/icon.component';
import {
  ProfessionalRole,
  RoleIssue,
  RoleRule,
  blockingRulesFor,
  evaluateRoles,
} from './role-compatibility';

/** Una spiegazione delle combinazioni vietate, condivisa dai ruoli che disabilita. */
interface BlockNote {
  readonly id: string;
  readonly text: string;
  readonly reference: string | null;
  readonly roles: readonly string[];
}

/** Oltre questa lunghezza la pillola mostra la sigla del ruolo (CSP, CSE), se c'è. */
const LONG_LABEL = 28;

const PILL =
  'inline-flex min-h-11 items-center gap-1.5 rounded-full border px-4 text-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink dark:focus-visible:outline-bsite-400';
const PILL_IDLE = `${PILL} border-stone-300 bg-white text-ink hover:bg-stone-50 dark:border-stone-700 dark:bg-stone-900 dark:text-stone-50 dark:hover:bg-stone-800`;
const PILL_SELECTED = `${PILL} border-ink bg-bsite-100 font-semibold text-ink dark:border-bsite-400 dark:bg-bsite-950 dark:text-bsite-100`;
const PILL_BLOCKED = `${PILL} cursor-not-allowed border-dashed border-stone-300 bg-stone-100 text-stone-600 line-through dark:border-stone-700 dark:bg-stone-800 dark:text-stone-300`;

/**
 * Scelta dei ruoli professionali nel cantiere (FR-014b, FR-015a…FR-015c): pillole a scelta
 * multipla con `aria-pressed`. Un ruolo incompatibile con quelli scelti resta raggiungibile ma
 * disabilitato (`aria-disabled`), con il motivo e il riferimento normativo collegati da
 * `aria-describedby`. Le combinazioni con avviso chiedono una conferma esplicita, che finisce in
 * `acknowledged` con i codici delle regole.
 */
@Component({
  selector: 'bs-role-picker',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent],
  host: { class: 'flex flex-col gap-3' },
  template: `
    <div
      role="group"
      [attr.aria-labelledby]="labelId"
      [attr.aria-describedby]="groupDescribedBy()"
      [attr.data-testid]="testId()"
      class="flex flex-col gap-3"
    >
      <p
        [id]="labelId"
        [class]="hideLabel() ? 'sr-only' : 'text-sm font-medium text-ink dark:text-stone-100'"
      >
        {{ label() }}
      </p>
      <ul class="flex flex-wrap gap-2">
        @for (role of options(); track role.code) {
          <li>
            <button
              type="button"
              [class]="role.selected ? selectedClass : role.blocked ? blockedClass : idleClass"
              [attr.aria-pressed]="role.selected"
              [attr.aria-disabled]="role.blocked || disabled() ? 'true' : null"
              [attr.aria-label]="role.short ? role.label : null"
              [attr.title]="role.short ? role.label : null"
              [attr.aria-describedby]="role.describedBy"
              [attr.data-role]="role.code"
              (click)="toggle(role.code, role.blocked)"
            >
              @if (role.selected) {
                <bs-icon name="check" [size]="16" />
              }
              {{ role.short ?? role.label }}
            </button>
          </li>
        }
      </ul>
    </div>

    @for (note of blockNotes(); track note.id) {
      <p
        [id]="note.id"
        class="text-sm text-stone-600 dark:text-stone-300"
        [attr.data-testid]="testId() ? testId() + '-blocked' : null"
      >
        {{ note.text }}
        @if (note.reference; as reference) {
          <span class="font-mono text-xs">({{ reference }})</span>
        }
      </p>
    }

    @for (issue of selectionBlocks(); track issue.rule) {
      <p
        role="alert"
        class="flex items-start gap-2 rounded-xl bg-red-50 p-3 text-sm text-red-800 dark:bg-red-950 dark:text-red-200"
        [attr.data-testid]="testId() ? testId() + '-incompatible' : null"
      >
        <bs-icon name="alert-circle" [size]="18" />
        <span>
          {{ describePair(issue) }}: {{ issue.message }}
          @if (issue.reference; as reference) {
            <span class="font-mono text-xs">({{ reference }})</span>
          }
        </span>
      </p>
    }

    @for (issue of warnings(); track issue.rule) {
      <div
        class="flex flex-col gap-2 rounded-xl bg-bsite-100 p-3 text-sm text-bsite-900 dark:bg-bsite-950 dark:text-bsite-100"
        [attr.data-testid]="testId() ? testId() + '-warning' : null"
        [attr.data-rule]="issue.rule"
      >
        <p class="flex items-start gap-2">
          <bs-icon name="warning" [size]="18" />
          <span>
            {{ describePair(issue) }}: {{ issue.message }}
            @if (issue.reference; as reference) {
              <span class="font-mono text-xs">({{ reference }})</span>
            }
          </span>
        </p>
        <label class="flex min-h-11 cursor-pointer items-center gap-3 pl-7 font-medium">
          <input
            type="checkbox"
            class="size-5 shrink-0 cursor-pointer accent-ink disabled:cursor-not-allowed dark:accent-bsite-400"
            [checked]="acknowledged().includes(issue.rule)"
            [disabled]="disabled()"
            [attr.data-testid]="testId() ? testId() + '-acknowledge' : null"
            (change)="acknowledge(issue.rule, $event)"
          />
          Confermo: la combinazione è corretta per questo cantiere
        </label>
      </div>
    }

    @if (error(); as message) {
      <p
        [id]="errorId"
        [class]="errorClass"
        [attr.data-testid]="testId() ? testId() + '-error' : null"
      >
        {{ message }}
      </p>
    }
  `,
})
export class RolePickerComponent {
  /** Catalogo dei ruoli, nell'ordine in cui mostrarli. */
  readonly roles = input.required<readonly ProfessionalRole[]>();
  readonly rules = input<readonly RoleRule[]>([]);
  /** Appalto pubblico effettivo: decide quali regole valgono. */
  readonly isPublic = input<boolean | null>(null);
  readonly label = input('Ruoli professionali');
  /** L'etichetta resta per i lettori di schermo ma non si vede (c'è già un titolo). */
  readonly hideLabel = input(false);
  readonly error = input<string | null>(null);
  readonly disabled = input(false);
  readonly testId = input<string | null>(null);
  /** I codici dei ruoli scelti. */
  readonly selected = model<readonly string[]>([]);
  /** I codici delle regole con avviso confermate dall'utente. */
  readonly acknowledged = model<readonly string[]>([]);

  protected readonly labelId = uniqueId('bs-role-picker');
  protected readonly errorId = `${this.labelId}-error`;
  protected readonly errorClass = FIELD_ERROR;
  protected readonly idleClass = PILL_IDLE;
  protected readonly selectedClass = PILL_SELECTED;
  protected readonly blockedClass = PILL_BLOCKED;

  private readonly labels = computed(
    () => new Map(this.roles().map((role) => [role.code, role.label])),
  );

  private readonly evaluation = computed(() =>
    evaluateRoles(this.selected(), this.isPublic(), this.roles(), this.rules()),
  );

  /** Combinazioni vietate già presenti nella scelta (per esempio dopo il cambio dell'appalto). */
  protected readonly selectionBlocks = computed(() => this.evaluation().blocks);
  protected readonly warnings = computed(() => this.evaluation().warnings);

  /** Le spiegazioni dei ruoli disabilitati, una per motivo e ruolo già scelto. */
  protected readonly blockNotes = computed<BlockNote[]>(() => {
    const selected = this.selected();
    const notes = new Map<string, { roles: string[]; rule: RoleRule; other: string }>();
    for (const role of this.roles()) {
      for (const rule of blockingRulesFor(role.code, selected, this.isPublic(), this.rules())) {
        const other = rule.roleA === role.code ? rule.roleB : rule.roleA;
        const key = `${rule.message}|${rule.reference ?? ''}|${other}`;
        const note = notes.get(key) ?? { roles: [], rule, other };
        if (!note.roles.includes(role.code)) {
          note.roles.push(role.code);
        }
        notes.set(key, note);
      }
    }
    return [...notes.values()].map((note, index) => ({
      id: `${this.labelId}-block-${index}`,
      roles: note.roles,
      reference: note.rule.reference,
      text: `${this.list(note.roles)} ${note.roles.length > 1 ? 'non sono compatibili' : 'non è compatibile'} con ${this.name(note.other)}: ${note.rule.message}`,
    }));
  });

  protected readonly options = computed(() => {
    const selected = this.selected();
    const notes = this.blockNotes();
    return this.roles().map((role) => {
      const ids = notes.filter((note) => note.roles.includes(role.code)).map((note) => note.id);
      return {
        code: role.code,
        label: role.label,
        short: role.label.length > LONG_LABEL && role.shortLabel ? role.shortLabel : null,
        selected: selected.includes(role.code),
        blocked: ids.length > 0,
        describedBy: ids.join(' ') || null,
      };
    });
  });

  protected readonly groupDescribedBy = computed(() => (this.error() ? this.errorId : null));

  protected toggle(code: string, blocked: boolean): void {
    if (blocked || this.disabled()) {
      return;
    }
    const current = this.selected();
    const next = current.includes(code)
      ? current.filter((item) => item !== code)
      : [...current, code];
    this.selected.set(next);
    // Le conferme valgono solo per gli avvisi ancora presenti.
    const warnings = evaluateRoles(next, this.isPublic(), this.roles(), this.rules()).warnings.map(
      (warning) => warning.rule,
    );
    const kept = this.acknowledged().filter((rule) => warnings.includes(rule));
    if (kept.length !== this.acknowledged().length) {
      this.acknowledged.set(kept);
    }
  }

  protected acknowledge(rule: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const current = this.acknowledged().filter((item) => item !== rule);
    this.acknowledged.set(checked ? [...current, rule] : current);
  }

  protected describePair(issue: RoleIssue): string {
    return `${this.name(issue.roles[0])} e ${this.name(issue.roles[1])}`;
  }

  private name(code: string): string {
    return this.labels().get(code) ?? code;
  }

  private list(codes: readonly string[]): string {
    const names = codes.map((code) => this.name(code));
    return names.length <= 1
      ? (names[0] ?? '')
      : `${names.slice(0, -1).join(', ')} e ${names[names.length - 1]}`;
  }
}
